import {
	addingSmartContractWalletAuthorities,
	loadingSmartContractWalletAuthorities,
	removingSmartContractWalletAuthorities,
	scWalletAccountResolver,
	smartContractWalletAuthorities,
	walletAddress
} from '$lib/wallet/store';
import { get } from 'svelte/store';
import {
	authorityAddressToAdd,
	checkingSmartContractWallet,
	smartContractWalletAddress,
	overrideSmartContractWalletAddress
} from './store';
import {
	getSmartContractWalletOwners,
	getUserSmartContractWallets
} from '$lib/web3/actions/scwFetchInfo';
import {
	addErrorNotification,
	addTransactionNotification
} from '$lib/notifications/notificationManager';
import { addAuthority, removeAuthority } from '$lib/web3/actions/scwModifiers';
import type { SendTransactionResult } from '@wagmi/core';
import { getParsedError } from '$lib/web3/errorHandling';
import { SC_EXECUTION_BOTS_CONTRACTS } from '$lib/web3/addresses';
import { popupModalOpen, popupModalType } from '$lib/popups/popupsStore';
import { triggerTosCheck } from '$lib/util/common';
import {
	destroUserDatarefresh,
	setupStablePriceDataRefresh,
	setupUserDataRefresh
} from '$lib/data/refreshData';
import { userBalances } from '../store';
import { userMarginList } from '../userInteract/store';

export async function checkUserSmartContractWallets(SC_WALLET_ACCOUNT_RESOLVER: `0x${string}`) {
	checkingSmartContractWallet.set(true);

	if (get(overrideSmartContractWalletAddress)) {
		smartContractWalletAddress.set(get(overrideSmartContractWalletAddress));
		setupUserDataRefresh();
		setupStablePriceDataRefresh();
		return;
	}
	if (SC_WALLET_ACCOUNT_RESOLVER === `0x`) {
		checkingSmartContractWallet.set(false);
		userBalances.set({});
		userMarginList.set([]);
		return;
	}
	const txData: Array<string> = await getUserSmartContractWallets(
		get(walletAddress),
		SC_WALLET_ACCOUNT_RESOLVER
	);

	if (txData.length > 0) {
		let isTosSigned = await triggerTosCheck(txData[0]);
		smartContractWalletAddress.set(txData[0]);

		setupUserDataRefresh();
		setupStablePriceDataRefresh();
	} else {
		destroUserDatarefresh();
		smartContractWalletAddress.set('');
		userBalances.set({});
		userMarginList.set([]);
	}
	checkingSmartContractWallet.set(false);
}

export async function checkSmartContractWalletOwners(
	overrideSmartContractWalletAddress: `0x${string}`,
	scWalletAccountResolver: `0x${string}`
): Promise<Array<string>> {
	const txData: Array<string> = await getSmartContractWalletOwners(
		overrideSmartContractWalletAddress,
		scWalletAccountResolver
	);
	return txData;
}

export async function setSmartContractWalletOwners() {
	try {
		loadingSmartContractWalletAuthorities.set(true);
		const txData: Array<string> = await getSmartContractWalletOwners(
			get(smartContractWalletAddress),
			get(scWalletAccountResolver)
		);
		smartContractWalletAuthorities.set(txData);
		loadingSmartContractWalletAuthorities.set(false);
	} catch (e: any) {
		const { title, description } = getParsedError(e);
		addErrorNotification(title, description, JSON.stringify(e));
		loadingSmartContractWalletAuthorities.set(false);
	}
}

export async function addSmartContractWalletAuthority(addressToAdd: `0x${string}`) {
	try {
		addingSmartContractWalletAuthorities.set(true);
		const txData: Array<string> = await getUserSmartContractWallets(
			addressToAdd,
			get(scWalletAccountResolver)
		);
		if (txData.length > 0) {
			addErrorNotification(
				'Error',
				'Address already linked to a smart contract wallet.',
				'The address that you are trying to add alreasy has a smart wallet linked to it. Currently only one smart wallet can be added per EOA however we do plan to lift this limitation very soon.'
			);
			addingSmartContractWalletAuthorities.set(false);
			return;
		} else {
			//send request to add authority to smart contract wallet
			const tx: SendTransactionResult = await addAuthority(
				get(smartContractWalletAddress) as `0x${string}`,
				addressToAdd as `0x${string}`
			);
			addTransactionNotification(
				'Authority Added',
				'New authoriy has been added to your sc wallet.',
				tx,
				async () => {
					await setSmartContractWalletOwners();
					addingSmartContractWalletAuthorities.set(false);
					authorityAddressToAdd.set(undefined);
					popupModalOpen.set(false);
				}
			);
		}
	} catch (e: any) {
		const { title, description } = getParsedError(e);
		addErrorNotification(title, description, JSON.stringify(e));
		addingSmartContractWalletAuthorities.set(false);
	}
}

export async function removeSmartContractWalletAuthority(addressToRemove: `0x${string}`) {
	try {
		removingSmartContractWalletAuthorities.set(true);
		if (SC_EXECUTION_BOTS_CONTRACTS.includes(addressToRemove)) {
			addErrorNotification(
				'Error',
				'Cannot remove execution bot.',
				'It is not possible to remove the execution bot from the smart contract wallet.'
			);
			removingSmartContractWalletAuthorities.set(false);
			return;
		} else {
			//send request to remove authority to smart contract wallet
			const tx: SendTransactionResult = await removeAuthority(
				get(smartContractWalletAddress) as `0x${string}`,
				addressToRemove as `0x${string}`
			);
			addTransactionNotification(
				'Authority Removed',
				'Authoriy has been removed from your sc wallet.',
				tx,
				async () => {
					await setSmartContractWalletOwners();
					removingSmartContractWalletAuthorities.set(false);
				}
			);
		}
	} catch (e: any) {
		const { title, description } = getParsedError(e);
		addErrorNotification(title, description, JSON.stringify(e));
		removingSmartContractWalletAuthorities.set(false);
	}
}

export function openAddAuthorityConfirmationModal() {
	popupModalType.set('ConfirmAddAuthority');
	popupModalOpen.set(true);
}
